/**
 * Client-side decision for the canonical-attribute NEAR-MISS advisory (task-po9ac21icv fix (3)).
 *
 * WHAT THE SERVER DOES. `POST /sectors/:sectorId/canonical-attributes` runs a deliberately BROAD
 * near-duplicate name detector and, on a hit, answers 409 with
 * `code: 'CANONICAL_ATTRIBUTE_NEAR_MISS'`, the colliding names in `near_misses`, and
 * `confirm_with: { confirm_near_miss: true }`. Resending the same body with that flag seats the
 * row. See backend-merged/services/canonicalAttributeWriteGuard.js.
 *
 * WHY THIS IS A SEPARATE MODULE. The advisory is only an advisory if the caller can CLEAR it.
 * Before this, the admin create flow threw every non-2xx body into a toast, so the 409 was a hard
 * block in the one place the detector fires — and the detector is broad on purpose (~90% of its
 * loose class are legitimate distinct facets: `material` + `material_density`, `size` +
 * `size_unit`), so blocking those was the failure mode rather than the feature. The decision lives
 * here, apart from the 4,800-line page component, so both directions can be pinned by a test that
 * actually runs. A one-directional test on a branch like this is the invisible-green shape: a
 * function that never returns 'confirm' still passes a suite that only checks the throw path, and
 * one that always returns 'confirm' still passes a suite that only checks the reject path.
 */

export interface CanonicalNearMiss {
  name: string;
  reason: string;
}

export type NearMissDecision =
  /** Open the confirmation dialog listing `nearMisses`; the operator adjudicates. */
  | { kind: 'confirm'; nearMisses: CanonicalNearMiss[] }
  /** Not an advisory (or an advisory we must not swallow) — surface `message` to the operator. */
  | { kind: 'surface'; message: string };

const NEAR_MISS_CODE = 'CANONICAL_ATTRIBUTE_NEAR_MISS';

const FALLBACK_MESSAGE = 'Failed to create attribute';

/**
 * Decide what a failed create response means.
 *
 * @param status          HTTP status of the failed response
 * @param body            the parsed JSON error body
 * @param alreadyConfirmed whether this attempt already carried `confirm_near_miss: true`
 *
 * Returns 'confirm' ONLY for a well-formed first-attempt advisory. Everything else surfaces:
 *
 *  - `alreadyConfirmed` — the server still reports a near miss after we sent the flag. That is a
 *    real backend disagreement, not an advisory. Re-opening the dialog would loop the operator
 *    through a confirmation that provably does not clear the block, so the disagreement is shown.
 *  - a NEAR_MISS 409 naming NO attributes — malformed, not a clean create. An empty confirm dialog
 *    would ask the operator to adjudicate nothing; treating it as clean would auto-clear an
 *    advisory the server did raise. It surfaces instead.
 */
export function decideNearMissResponse(
  status: number,
  body: any,
  alreadyConfirmed: boolean
): NearMissDecision {
  const message = (body && typeof body.error === 'string' && body.error) || FALLBACK_MESSAGE;

  if (status !== 409 || !body || body.code !== NEAR_MISS_CODE || alreadyConfirmed) {
    return { kind: 'surface', message };
  }

  const nearMisses: CanonicalNearMiss[] = Array.isArray(body.near_misses)
    ? body.near_misses.filter(
        (m: any) => m && typeof m.name === 'string' && m.name.trim() !== ''
      )
    : [];

  if (nearMisses.length === 0) {
    return {
      kind: 'surface',
      message:
        `${message} (server reported a near miss but named none — refusing to offer a `
        + 'confirmation for an unstated conflict)',
    };
  }

  return { kind: 'confirm', nearMisses };
}
